<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  knowledgebaseId: {
    type: String,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const query = ref('');
const maxStops = ref(5);
const searchType = ref('semantic');
const profile = ref('driving');
const startLat = ref('');
const startLon = ref('');
const endLat = ref('');
const endLon = ref('');

const isSearching = ref(false);
const results = ref(null);
const error = ref(null);

const searchTypes = [
  { value: 'semantic', label: t('LIBRARIES.ITINERARY.SEARCH_TYPES.SEMANTIC') },
  { value: 'hybrid', label: t('LIBRARIES.ITINERARY.SEARCH_TYPES.HYBRID') },
  { value: 'keyword', label: t('LIBRARIES.ITINERARY.SEARCH_TYPES.KEYWORD') },
];

const profiles = [
  { value: 'driving', label: t('LIBRARIES.ITINERARY.PROFILES.DRIVING') },
  { value: 'walking', label: t('LIBRARIES.ITINERARY.PROFILES.WALKING') },
  { value: 'cycling', label: t('LIBRARIES.ITINERARY.PROFILES.CYCLING') },
  {
    value: 'driving-traffic',
    label: t('LIBRARIES.ITINERARY.PROFILES.DRIVING_TRAFFIC'),
  },
];

const handleSearch = async () => {
  isSearching.value = true;
  error.value = null;
  results.value = null;

  const params = {
    id: props.knowledgebaseId,
    query: query.value,
    max_stops: Number(maxStops.value),
    search_type: searchType.value,
    profile: profile.value,
  };

  if (startLat.value && startLon.value) {
    params.start = {
      lat: parseFloat(startLat.value),
      lon: parseFloat(startLon.value),
    };
  }

  if (endLat.value && endLon.value) {
    params.end = {
      lat: parseFloat(endLat.value),
      lon: parseFloat(endLon.value),
    };
  }

  try {
    results.value = await store.dispatch(
      'knowledgebase/itinerarySearchAction',
      params
    );
  } catch {
    error.value = t('LIBRARIES.ITINERARY.ERROR');
  } finally {
    isSearching.value = false;
  }
};

const formatDuration = seconds => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  return mins > 0 ? `${mins}min` : `${Math.round(seconds)}s`;
};
</script>

<template>
  <div class="flex-1 h-full flex flex-col pt-6 overflow-y-auto">
    <div
      class="bg-slate-25 dark:bg-slate-900 pt-4 pb-0 px-8 border-b border-solid border-slate-50 dark:border-slate-800/50 flex items-center gap-3"
    >
      <h2 class="text-2xl text-slate-800 dark:text-slate-100 mb-1 font-medium">
        {{ t('LIBRARIES.ITINERARY.TITLE') }}
      </h2>
      <span
        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 mb-1"
      >
        {{ t('LIBRARIES.ITINERARY.BETA') }}
      </span>
    </div>

    <div class="p-8 max-w-2xl">
      <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
        {{ t('LIBRARIES.ITINERARY.DESCRIPTION') }}
      </p>

      <form class="space-y-4" @submit.prevent="handleSearch">
        <!-- Query -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('LIBRARIES.ITINERARY.QUERY') }}
          </label>
          <input
            v-model="query"
            type="text"
            :placeholder="t('LIBRARIES.ITINERARY.QUERY_PLACEHOLDER')"
            class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <!-- Max Stops / Search Type / Profile -->
        <div class="grid grid-cols-3 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('LIBRARIES.ITINERARY.MAX_STOPS') }}
            </label>
            <input
              v-model="maxStops"
              type="number"
              min="1"
              max="12"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('LIBRARIES.ITINERARY.SEARCH_TYPE') }}
            </label>
            <select
              v-model="searchType"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option
                v-for="st in searchTypes"
                :key="st.value"
                :value="st.value"
              >
                {{ st.label }}
              </option>
            </select>
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('LIBRARIES.ITINERARY.PROFILE') }}
            </label>
            <select
              v-model="profile"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option v-for="p in profiles" :key="p.value" :value="p.value">
                {{ p.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Start point -->
        <div>
          <p
            class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            {{ t('LIBRARIES.ITINERARY.START_POINT') }}
            <span class="font-normal text-slate-500">
              {{ t('LIBRARIES.ITINERARY.OPTIONAL') }}
            </span>
          </p>
          <div class="grid grid-cols-2 gap-4">
            <input
              v-model="startLat"
              type="number"
              step="any"
              :placeholder="t('LIBRARIES.ITINERARY.START_LAT')"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              v-model="startLon"
              type="number"
              step="any"
              :placeholder="t('LIBRARIES.ITINERARY.START_LON')"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <!-- End point -->
        <div>
          <p
            class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
          >
            {{ t('LIBRARIES.ITINERARY.END_POINT') }}
            <span class="font-normal text-slate-500">
              {{ t('LIBRARIES.ITINERARY.OPTIONAL') }}
            </span>
          </p>
          <div class="grid grid-cols-2 gap-4">
            <input
              v-model="endLat"
              type="number"
              step="any"
              :placeholder="t('LIBRARIES.ITINERARY.END_LAT')"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              v-model="endLon"
              type="number"
              step="any"
              :placeholder="t('LIBRARIES.ITINERARY.END_LON')"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <Button
          type="submit"
          :label="t('LIBRARIES.ITINERARY.SEARCH_BUTTON')"
          icon="i-lucide-route"
          :disabled="isSearching"
          :is-loading="isSearching"
        />
      </form>

      <!-- Error -->
      <div
        v-if="error"
        class="mt-6 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm"
      >
        {{ error }}
      </div>

      <!-- Results -->
      <div v-if="results" class="mt-6">
        <!-- Summary -->
        <div
          class="mb-4 p-3 rounded-md bg-slate-50 dark:bg-slate-800 text-xs text-slate-500 dark:text-slate-400 space-y-1"
        >
          <div v-if="results.query_info?.stops_in_route">
            {{
              t('LIBRARIES.ITINERARY.STOPS_FOUND', {
                count: results.query_info.stops_in_route,
              })
            }}
          </div>
          <div v-if="results.total_distance_m">
            {{
              t('LIBRARIES.ITINERARY.TOTAL_DISTANCE', {
                distance: Math.round(results.total_distance_m),
              })
            }}
          </div>
          <div v-if="results.total_duration_s">
            {{
              t('LIBRARIES.ITINERARY.TOTAL_DURATION', {
                duration: formatDuration(results.total_duration_s),
              })
            }}
          </div>
        </div>

        <!-- Stops -->
        <div v-if="results.stops?.length" class="space-y-3">
          <div
            v-for="stop in results.stops"
            :key="stop.order"
            class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 relative"
          >
            <!-- Order badge -->
            <div
              class="absolute -left-3 top-4 w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-semibold"
            >
              {{ stop.order + 1 }}
            </div>
            <div class="pl-4">
              <h3
                class="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                {{
                  stop.document?.metadata?.title ||
                  stop.document?.page_content?.slice(0, 60) ||
                  '—'
                }}
              </h3>
              <p
                v-if="stop.document?.page_content"
                class="mt-1 text-xs text-slate-600 dark:text-slate-400 line-clamp-2"
              >
                {{ stop.document.page_content }}
              </p>
              <div
                v-if="stop.distance_to_next_m || stop.duration_to_next_s"
                class="mt-2 flex items-center gap-3 text-xs text-slate-400 dark:text-slate-500"
              >
                <span v-if="stop.distance_to_next_m">
                  {{
                    t('LIBRARIES.ITINERARY.DISTANCE_TO_NEXT', {
                      distance: Math.round(stop.distance_to_next_m),
                    })
                  }}
                </span>
                <span v-if="stop.duration_to_next_s">
                  {{
                    t('LIBRARIES.ITINERARY.DURATION_TO_NEXT', {
                      duration: formatDuration(stop.duration_to_next_s),
                    })
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <p v-else class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('LIBRARIES.ITINERARY.NO_RESULTS') }}
        </p>
      </div>
    </div>
  </div>
</template>
